import { useEffect, useRef } from "react";
import { startVisibilityAwarePoller } from "@/services/visibilityAwarePoll";

interface UseVisibilityAwarePollOptions {
  readonly enabled?: boolean;
  readonly criticalWhileHidden?: boolean;
  readonly runImmediately?: boolean;
}

/**
 * Runs `callback` on a recurring `intervalMs` cadence, pausing while the tab is hidden unless
 * `criticalWhileHidden` is set. See `startVisibilityAwarePoller` for the scheduling engine this
 * wraps (kept framework-agnostic and unit-tested separately).
 */
const useVisibilityAwarePoll = (
  callback: () => void | Promise<void>,
  intervalMs: number,
  options: UseVisibilityAwarePollOptions = {}
): void => {
  const { enabled = true, criticalWhileHidden = false, runImmediately = false } = options;
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    return startVisibilityAwarePoller(() => callbackRef.current(), intervalMs, { criticalWhileHidden, runImmediately });
  }, [criticalWhileHidden, enabled, intervalMs, runImmediately]);
};

export default useVisibilityAwarePoll;
